import { inject } from '@angular/core';
import { HttpInterceptorFn } from '@angular/common/http';
import { HttpErrorResponse } from '@angular/common/http';
import { EMPTY, throwError, catchError, switchMap, timer } from 'rxjs';
import { retryWhen, take, delayWhen } from 'rxjs/operators';
import { AuthService } from '../../auth/auth.service';
import { AlertService } from '../service/alert.service';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
    const authService = inject(AuthService);
    const alertService = inject(AlertService);
    const token = authService.getToken();
    const lang = localStorage.getItem('language') || 'en';

    // Safely get tenant data
    let getTenant = '';
    try {
        const tenantData = authService.getTenanatDataValue();
        getTenant = (tenantData && typeof tenantData === 'object' && tenantData.tenant_subdomain) || '';
    } catch (error) {
        getTenant = '';
    }

    // Public third-party APIs / S3 must not receive auth/tenant headers (CORS preflight fails).
    const isExternalPublicApi =
        req.url.includes('api.frankfurter.dev') ||
        req.url.includes('api.fastforex.io') ||
        req.url.includes('amazonaws.com') ||
        req.url.includes('.s3.');
    if (isExternalPublicApi) {
        return next(req);
    }

    // Skip token refresh attempts for certain paths
    const skipTokenRefresh =
        req.url.includes('/auth/vendor/login') || req.url.includes('/auth/vendor/refresh-token') || req.url.includes('/auth/forgot-password');

    // Skip tenant header for certain paths
    const skipTenantHeader =
        req.url.includes('/login') ||
        // req.url.includes('/profile') ||
        //  req.url.includes('/auth/') ||
        req.url.includes('/forgot-password');

    // Helper function to build headers
    const buildHeaders = (token?: string | null) => {
        const headers: any = {
            'x-custom-lang': lang,
            'x-tenant-id': 'sourceby',
        };

        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        if (!skipTenantHeader) {
            // Only add tenant header if tenant ID is available
            if (getTenant && getTenant !== 'select_tenant') {
                headers['x-tenant-id'] = getTenant;
            } else {
                // Log warning for missing tenant ID on protected routes
            }
        }

        return headers;
    };

    // Note: Removed proactive token refresh - let session service handle timing
    // Only refresh tokens reactively when server returns 401 errors

    // Clone the request to add the Authorization header
    const authReq = req.clone({
        setHeaders: buildHeaders(token),
    });

    return next(authReq).pipe(
        // Retry mechanism with exponential backoff for server errors
        retryWhen((errors) =>
            errors.pipe(
                delayWhen((error: HttpErrorResponse, index: number) => {
                    // Only retry on specific server errors (500, 502, 503, 504)
                    if ([500, 502, 503, 504].includes(error.status) && index < 3) {
                        // Exponential backoff: 1s, 2s, 4s
                        return timer(Math.pow(2, index) * 1000);
                    }
                    return throwError(() => error);
                }),
                take(3) // Maximum 3 retries
            )
        ),
        catchError((error: HttpErrorResponse) => {
              const errorMessage =
        error?.error?.message ||
        error?.error?.error ||
        'Unauthorized';
            // Check for 401 Unauthorized errors
            if (error.status === 401 && !skipTokenRefresh) {
                // Try to refresh the token (similar to new-admin implementation)
                return authService.refreshToken().pipe(
                    switchMap(() => {
                        // Token refreshed successfully, retry original request
                        const newToken = authService.getToken();
                        const retryReq = req.clone({
                            setHeaders: buildHeaders(newToken),
                        });
                        return next(retryReq);
                    }),
                    catchError(() => {
                        // Refresh failed: redirect to sign-in and stop propagating raw API text (e.g. "jwt expired").
                        authService.handleSessionExpiration(errorMessage);
                        return EMPTY;
                    })
                );
            }

            // Handle network errors during server downtime
            if (error.status === 0 || error.status === 504) {
                alertService.showAlert('Connection Error', 'Server is temporarily unavailable. Please try again in a few moments.', 'warning');
            }

            // Skip when this request is an auth flow we handle elsewhere (login, refresh, forgot-password)
            if (error.status === 401 && !skipTokenRefresh && req.url.includes('/auth/')) {
                alertService.showAlert('Authentication Error', 'Please check your credentials and try again.', 'error');
            }

            // For other errors or if token refresh is skipped, just pass through the error
            return throwError(() => error);
        })
    );
};
